import { fDate } from '@/utils/dayjs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { fCurrency } from '@/utils/format-number'
import { Card, CardContent } from '@/components/ui/card'
import { getFirstImageMedia } from '@/utils/get-first-image-media'
import { Edit, MoreHorizontal, Image as ImageIcon } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { Product } from './product.types'

interface ProductsTableProps {
  products: Product[]
  onEdit: (productId: string) => void
  onDelete?: (productId: string) => void
  loading?: boolean
}

export default function ProductsTable({
  products,
  onEdit,
  // onDelete,
  loading = false,
}: ProductsTableProps) {
  // แสดง status badge
  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <Badge variant="default" className="bg-green-500 text-background">
          เปิดใช้งาน
        </Badge>
      )
    }
    return (
      <Badge variant="secondary" className="bg-red-500 text-background">
        ปิดใช้งาน
      </Badge>
    )
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>รูปภาพ</TableHead>
                <TableHead>ชื่อสินค้า</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>ราคา</TableHead>
                <TableHead>คงเหลือ</TableHead>
                <TableHead>วันที่แก้ไข</TableHead>
                <TableHead className="text-right">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="w-12 h-12 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse ml-auto" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  if (products.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-base text-mute">ไม่พบข้อมูลสินค้า</p>
      </div>
    )
  }

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>รูปภาพ</TableHead>
              <TableHead>ชื่อสินค้า</TableHead>
              <TableHead>สถานะ</TableHead>
              <TableHead>ราคา</TableHead>
              <TableHead>คงเหลือ</TableHead>
              <TableHead>วันที่แก้ไข</TableHead>
              <TableHead className="text-right">จัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => {
              // Find the first image media, or undefined if not found
              const imageMedia = getFirstImageMedia(product.mediaUrls)

              return (
                <TableRow key={product.id}>
                  <TableCell>
                    <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                      {imageMedia ? (
                        <img
                          src={imageMedia}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-base font-medium text-black line-clamp-2 max-w-[200px] truncate">
                        {product.name}
                      </p>
                      {product.description && (
                        <p className="text-sm text-mute line-clamp-2 max-w-[200px] truncate">
                          {product.description}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(product.isActive)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-base font-medium text-black">
                        {fCurrency(product.price)}
                      </p>
                      {product?.discount && product?.discount > 0 && (
                        <p className="text-sm text-success">
                          ลด {fCurrency(product.discount)}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-base text-black">
                      {product.stock}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-mute">
                      {fDate(product.updatedAt)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(product.id)}>
                          <Edit className="mr-2 h-4 w-4" />
                          แก้ไข
                        </DropdownMenuItem>
                        {/* {onDelete && (
                          <DropdownMenuItem
                            onClick={() => onDelete(product.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            ลบ
                          </DropdownMenuItem>
                        )} */}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
